import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Box, Button, Container, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import ToolbarSpacer from 'components/shared/ToolbarSpacer';
import TiledHighlightsView from './TiledHighlightsView';
import Add from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import NoHighlightsPlaceholder from './NoHighlightsPlaceholder';
import CreateSessionHighlightModal from './CreateSessionHighlight/CreateSessionHighlightModal';
import HighlightModal from './HighlightModal';
import useSessionHighlights from '../useSessionHighlights';

const HighlightsPaneRoot = styled('div')(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 500,
    backgroundColor: '#102A43',
    transition: 'opacity 400ms',
    color: 'white',
    overflow: 'auto',
    '& .MuiContainer-root': {
        paddingTop: 16,
        paddingBottom: 16
    }
}));

type HighlightsPaneProps = {
    activityId?: string
    isOpen?: boolean
    onCloseBtnClick?: (...args: unknown[]) => unknown
    onHighlightCreated?: (...args: unknown[]) => unknown
    recordingId?: string
    videoRecordingUrl?: string
};

const HighlightsPane = ({
    activityId,
    recordingId,
    videoRecordingUrl,
    isOpen,
    onCloseBtnClick
}: HighlightsPaneProps) => {
    const { t } = useTranslation();
    const { data: sessionHighlights, refetch: refetchSessionHighlights } = useSessionHighlights(activityId);

    const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
    const [selectedHighlight, setSelectedHighlight] = useState(null);



    // Reload session highlights when the highlights pane is 'opened'
    useEffect(() => {
        if (isOpen === true) {
            refetchSessionHighlights();
        }
    // TODO - investigate the implications of this one and whether it causes any issuer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const renderHighlights = () => {
        if (sessionHighlights?.length > 0) {
            return (
                <TiledHighlightsView
                    highlights={sessionHighlights}
                    onSelectHighlight={(highlight) => setSelectedHighlight(highlight)}
                />
            );
        } else {
            return (
                <NoHighlightsPlaceholder onCreateHighlightBtnClick={() => setIsCreatingHighlight(true)} />
            );
        }
    };

    return (
        <HighlightsPaneRoot sx={{
            visibility: isOpen ? 'visible' : 'hidden',
            opacity: isOpen ? 1 : 0,
        }}>
            <Box sx={{
                padding: 2,
                paddingLeft: 4,
                paddingRight: 4,
                display: 'flex',
                alignItems: 'center',
                '& .MuiButton-root': {
                    backgroundColor: 'white',
                    color: '#102A43'
                }
            }}>
                <Typography variant="h6">
                    { t('sessionHighlights.text.sessionHighlightsTitle') }
                </Typography>

                <ToolbarSpacer grow />

                { sessionHighlights?.length > 0 &&
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setIsCreatingHighlight(true)}
                    >
                        { t('sessionHighlights.buttons.createHighlight') }
                    </Button>
                }

                <ToolbarSpacer large />

                <IconButton color="inherit" onClick={onCloseBtnClick} size="large">
                    <Close />
                </IconButton>
            </Box>
            <Container maxWidth={'xl'}>
                { renderHighlights() }
            </Container>
            <CreateSessionHighlightModal
                recordingId={recordingId}
                isOpen={isCreatingHighlight}
                onClose={() => setIsCreatingHighlight(false)}
                videoRecordingUrl={videoRecordingUrl}
                onHighlightCreated={() => {
                    setIsCreatingHighlight(false);
                    refetchSessionHighlights();
                }}
            />
            { selectedHighlight &&
                <HighlightModal
                    activityId={activityId}
                    highlightId={selectedHighlight.id}
                    title={selectedHighlight.title}
                    description={selectedHighlight.description}
                    isOpen={Boolean(selectedHighlight)}
                    onClose={() => setSelectedHighlight(null)}
                    videoUri={selectedHighlight.videoUri}
                    onHighlightDeleted={() => {
                        setSelectedHighlight(null);
                        refetchSessionHighlights();
                    }}
                />
            }
        </HighlightsPaneRoot>
    );
};

export default HighlightsPane;
